import React, { useEffect, useMemo, useRef } from 'react'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useDispatch, useSelector } from 'react-redux'
import { BrowserRouter as Router } from 'react-router-dom'
import { bindActionCreators } from 'redux'
import { SWRConfig } from 'swr'

import { Classification, Inbox, Team } from '@driftt/api-types'
import { ThemeProvider as DDSThemeProvider, ToastProvider } from '@driftt/dds'
import { DriftAnalytics } from '@driftt/dds.analytics'
import { CubeDataProvider } from '@driftt/dds.reports'

import { fetchInboxes } from 'api/conversation'
import {
  fetchClassifications,
  fetchCutover,
  fetchOrgTags,
  fetchOrgTeams,
} from 'api/customer'
import config from 'config'
import * as Actions from 'modules/cutovers/actions'
import * as OrgSelectors from 'modules/org/selectors'
import * as UserSelectors from 'modules/user/selectors'
import { TideSelectOption } from 'types/tide'
import { createReceiveAction } from 'utils/redux'

import App from './component'

const fetchAndReceive = <T extends object>(
  promise: Promise<T>,
  receieveAction: (value: T) => void
) =>
  promise.then(receieveAction).catch((error) => {
    console.error('Fetch error:', error)
  })
const queryClient = new QueryClient()

const AppContainer = () => {
  const initialized = useRef(false)
  const dispatch = useDispatch()
  const orgId = useSelector(OrgSelectors.orgId)
  const currentUser = useSelector(UserSelectors.currentUser)
  const actions = useMemo(
    () =>
      bindActionCreators(
        {
          receiveClassifications:
            createReceiveAction<TideSelectOption[]>('classifications'),
          receiveInboxes: createReceiveAction<Inbox[]>('inboxes'),
          receiveTags: createReceiveAction('tags'),
          receiveTeams: createReceiveAction<Team[]>('teams'),
          receiveCutovers: Actions.receiveCutoverNamespace,
        },
        dispatch
      ),
    [dispatch]
  )

  useEffect(() => {
    if (initialized.current) {
      return
    }

    if (!orgId) {
      return
    }

    fetchAndReceive(fetchCutover(orgId, 'COVERAGE_REPORT'), (data) => {
      actions.receiveCutovers(data)
    })

    fetchAndReceive(
      fetchClassifications(),
      (classifications: Classification[]) => {
        actions.receiveClassifications(
          classifications.map(({ name: label, id: value }) => ({
            label,
            value,
          }))
        )
      }
    )

    fetchAndReceive(fetchInboxes(), actions.receiveInboxes)
    fetchAndReceive(fetchOrgTags(orgId), actions.receiveTags)
    fetchAndReceive(fetchOrgTeams(), actions.receiveTeams)

    DriftAnalytics.initialize({
      user: currentUser,
    })

    initialized.current = true
  }, [orgId, initialized]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <SWRConfig
      value={{
        dedupingInterval: 60000,
        errorRetryInterval: 30000,
      }}
    >
      <DDSThemeProvider>
        <QueryClientProvider client={queryClient}>
          <ToastProvider>
            <CubeDataProvider>
              <Router basename={config.APP_ROOT}>
                <App />
              </Router>
            </CubeDataProvider>
          </ToastProvider>
        </QueryClientProvider>
      </DDSThemeProvider>
    </SWRConfig>
  )
}

export default AppContainer
